/**
 * Shared UI tokens for the Live Fleet Map dashboard.
 *
 * Why this file exists:
 * The dashboard mixes three different text-rendering contexts that can't all
 * use <MDTypography variant="..."> -
 *   1. Plain MUI components (sx prop)
 *   2. Raw SVG (the speedometer gauge)
 *   3. Google Maps overlays (InfoWindow content, Marker labels) which only
 *      accept plain CSS strings, not React style props.
 *
 * Previously each file invented its own pixel size for things like "small
 * caption text" (8px, 10px, 11px, 0.7rem, 0.75rem all doing the same job in
 * different places). FONT_SIZE below is the single source of truth: every
 * component imports from here instead of hard-coding a number, so the same
 * semantic text role always renders at the same size everywhere.
 */

// ---- Typography scale (semantic name -> rem, with the px equivalent noted) ----
export const FONT_SIZE = {
  micro: "0.625rem", // 10px - tiny overlay badges (map marker numbers, fps overlay)
  caption: "0.6875rem", // 11px - secondary meta text, pill labels
  small: "0.75rem", // 12px - default caption / helper text
  body: "0.8125rem", // 13px - default running text in cards and lists
  medium: "0.875rem", // 14px - emphasized body text, form controls
  subtitle: "0.9375rem", // 15px - card / row titles
  title: "1.0625rem", // 17px - section titles
  display: "1.375rem", // 22px - large numeric readouts (e.g. speedometer)
};

// Pixel mirror of the scale above, for contexts that need a raw number
// (canvas/SVG text, Google Maps marker label `fontSize`, etc.)
export const FONT_SIZE_PX = {
  micro: 10,
  caption: 11,
  small: 12,
  body: 13,
  medium: 14,
  subtitle: 15,
  title: 17,
  display: 22,
};

export const FONT_WEIGHT = {
  regular: 400,
  medium: 500,
  bold: 700,
};

// ---- Spacing / radius / shadow scale ----
export const RADIUS = {
  sm: 8,
  md: 12,
  lg: 20,
  pill: 999,
};

export const SHADOW = {
  sm: "0 2px 8px rgba(15, 23, 42, 0.08)",
  md: "0 6px 20px rgba(15, 23, 42, 0.12)",
  lg: "0 10px 32px rgba(15, 23, 42, 0.18)",
  floating: "0 4px 14px rgba(15, 23, 42, 0.18)",
};

// ---- Brand accent (used by camera / playback modals & status pills) ----
export const BRAND = {
  accent: "#FB8C00",
  accentHover: "#EF6C00",
  success: "#2BBF9F",
  danger: "#E63946",
  offline: "#9E9E9E",
};

/**
 * ---- "Signal" visual identity ----
 * The product's subject is live telemetry: vehicles reporting position,
 * ignition, and speed in real time. The brand thread is a cyan -> violet
 * "signal" gradient (movement / connectivity) used sparingly as an accent
 * spine, active-state ring, and progress fill — never as a full background.
 */
export const SIGNAL = {
  cyan: "#22D3EE",
  violet: "#8B5CF6",
  gradient: "linear-gradient(135deg, #22D3EE 0%, #6366F1 55%, #8B5CF6 100%)",
  gradientSoft: "linear-gradient(135deg, rgba(34,211,238,0.16) 0%, rgba(139,92,246,0.16) 100%)",
};

// One color story per fleet status — used by stat cards, vehicle card accent
// rails, and map markers so the same status always reads the same way.
export const STATUS = {
  total: {
    base: "#6366F1",
    gradient: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
    soft: "rgba(99, 102, 241, 0.12)",
    glow: "rgba(99, 102, 241, 0.35)",
  },
  running: {
    base: "#10B981",
    gradient: "linear-gradient(135deg, #34D399 0%, #059669 100%)",
    soft: "rgba(16, 185, 129, 0.12)",
    glow: "rgba(16, 185, 129, 0.35)",
  },
  onroute: {
    base: "#22D3EE",
    gradient: "linear-gradient(135deg, #22D3EE 0%, #0891B2 100%)",
    soft: "rgba(34, 211, 238, 0.12)",
    glow: "rgba(34, 211, 238, 0.35)",
  },
  idle: {
    base: "#F59E0B",
    gradient: "linear-gradient(135deg, #FBBF24 0%, #D97706 100%)",
    soft: "rgba(245, 158, 11, 0.12)",
    glow: "rgba(245, 158, 11, 0.35)",
  },
  parked: {
    base: "#E63946",
    gradient: "linear-gradient(135deg, #F87171 0%, #DC2626 100%)",
    soft: "rgba(230, 57, 70, 0.12)",
    glow: "rgba(230, 57, 70, 0.35)",
  },
  offline: {
    base: "#94A3B8",
    gradient: "linear-gradient(135deg, #CBD5E1 0%, #64748B 100%)",
    soft: "rgba(100, 116, 139, 0.12)",
    glow: "rgba(100, 116, 139, 0.35)",
  },
};

// ---- Glass-panel surfaces (light / dark) ----
export const GLASS = {
  light: {
    panel: "rgba(255, 255, 255, 0.86)",
    panelSolid: "#FFFFFF",
    border: "rgba(15, 23, 42, 0.08)",
    inset: "rgba(15, 23, 42, 0.035)",
  },
  dark: {
    panel: "rgba(21, 27, 46, 0.86)",
    panelSolid: "#1a2136",
    border: "rgba(255, 255, 255, 0.08)",
    inset: "rgba(255, 255, 255, 0.04)",
  },
};

// ---- Shared breakpoint-based size helpers ----
// Use with the MUI `sx` responsive-object syntax, e.g. sx={{ width: SIDEBAR_WIDTH }}
export const SIDEBAR_WIDTH = { xs: "100%", sm: "100%", md: 360, lg: 400, xl: 460 };
export const MODAL_WIDTH_SM = { xs: "92vw", sm: 420, md: 460 };
export const MODAL_WIDTH_MD = { xs: "92vw", sm: 480, md: 640, lg: 760 };

export default {
  FONT_SIZE,
  FONT_SIZE_PX,
  FONT_WEIGHT,
  RADIUS,
  SHADOW,
  BRAND,
  SIGNAL,
  STATUS,
  GLASS,
  SIDEBAR_WIDTH,
  MODAL_WIDTH_SM,
  MODAL_WIDTH_MD,
};